import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';

import SBSMonitoringFilter from './FilterSBS/FilterSBSMonitoringReport';

import useGetSBSMonitoring from 'hooks/Reports/SBSMonitoring/SBSMonitoring';
import './SBSMonitoring.scss';
import { TrapActivityType } from 'api/traps/SRBMTraps/types';

const SBSMonitoring: React.FC<{ slug: string }> = ({ slug }) => {
  const { t } = useTranslation();

  const initialStartDate = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [filterType, setFilterType] = useState<string>('default');

  const { data, refetch, isRefetching, isLoading } = useGetSBSMonitoring(slug, startDate, endDate, filterType);
  const [isDateChanged, setIsDateChanged] = useState(false);

  const getStatus = (activityType: TrapActivityType) => {
    switch (activityType) {
      case TrapActivityType.ACTIVITY:
        return t('VisitReportsPDF.monitoring-bait-table.activity');
      case TrapActivityType.CONSUMPTION:
        return t('VisitReportsPDF.monitoring-bait-table.consumption');
    }
  };

  const getStatusColor = (activityType: TrapActivityType) => {
    switch (activityType) {
      case TrapActivityType.ACTIVITY:
        return 'warning';
      case TrapActivityType.CONSUMPTION:
        return 'success';
    }
  };

  useEffect(() => {
    if (!isDateChanged) {
      refetch();
    }
  }, [startDate, endDate, refetch, isDateChanged, filterType]);

  const handleLastMonth = () => {
    setStartDate(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleCurrentMonth = () => {
    setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleApplyDateChange = () => {
    setIsDateChanged(false);
    refetch();
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsDateChanged(true);
  };

  return (
    <div className="bg-white">
      <div className="card">
        <div className="card-body">
          <div className="row my-3">
            <div className="col-md-12">
              <div className="d-flex flex-column flex-lg-row justify-content-center justify-content-lg-between align-items-center my-3 gap-3">
                <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0 align-items-center">
                  <div className="d-flex flex-column flex-lg-row gap-1 gap-lg-0 align-items-center ">
                    <input
                      type="date"
                      className="form-control mr-2"
                      value={startDate}
                      onChange={e => handleDateChange(e.target.value, endDate)}
                    />
                    <input
                      type="date"
                      className="form-control mx-2"
                      value={endDate}
                      onChange={e => handleDateChange(startDate, e.target.value)}
                    />

                    <button
                      className="btn btn-ghost-primary rounded-pill mx-2"
                      style={{ width: '200px' }}
                      onClick={handleApplyDateChange}
                    >
                      Apply
                    </button>
                  </div>
                  <div className="d-flex gap-2 mt-2 mt-lg-0">
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleLastMonth}>
                      {moment().subtract(1, 'months').format('MMMM')}
                    </button>
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleCurrentMonth}>
                      {moment().format('MMMM')}
                    </button>
                  </div>
                </div>

                <SBSMonitoringFilter filterType={filterType} setFilterType={setFilterType} />
              </div>
            </div>
          </div>
          {isRefetching || isLoading ? (
            <div className="row">
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1001,
                  height: '70vh',
                }}
              >
                <ColorRing
                  visible
                  height="80"
                  width="80"
                  ariaLabel="color-ring-loading"
                  wrapperStyle={{}}
                  wrapperClass="color-ring-wrapper"
                  colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
                />
              </div>
            </div>
          ) : (
            <div className="row" style={{ overflowX: 'auto' }}>
              <div className="table-wrapper" style={{ minWidth: '1440px', maxWidth: '100%', overflowX: 'auto' }}>
                <table className="sbs-table">
                  <thead className="sbs-thead">
                    <tr className="ml-th-bordered-bottom-dark">
                      <th className="text-center mct-th table-sticky-header" scope="col">
                        {t('MonitoringBait.chieldAccordion.sequenceNumber')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('MonitoringBait.chieldAccordion.usedRodenticide')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.signalDate')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.signalTime')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.RodenticideAmount')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.eaten')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.added')}
                      </th>
                      <th className="text-center sbs-th table-sticky-header" scope="col">
                        {t('VisitReportsPDF.monitoring-bait-table.eventStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="sbs-tbody">
                    {data && data.length > 0 ? (
                      data.map((trapMonitroingData, index) =>
                        trapMonitroingData.signals.length > 0 ? (
                          (trapMonitroingData.signals ?? [])
                            .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
                            .map((signal, signalIndex) =>
                              signal.time
                                .sort((a, b) => b.inner_time.localeCompare(a.inner_time))
                                .map((time, timeIndex) => (
                                  <tr
                                    className=""
                                    key={`${trapMonitroingData.sequence_number}-${trapMonitroingData.date_of_installation}-${index}-${signal.date}-${timeIndex}`}
                                  >
                                    {signalIndex === 0 && timeIndex === 0 && (
                                      <>
                                        <td
                                          className="text-center align-top sbs-td"
                                          rowSpan={trapMonitroingData.signals.reduce(
                                            (acc, cur) => acc + cur.time.length,
                                            0,
                                          )}
                                        >
                                          <div className="sticky-cell">
                                            {trapMonitroingData.sequence_number || 'N/A'}
                                          </div>
                                        </td>
                                        <td
                                          className="text-center align-top sbs-td"
                                          rowSpan={trapMonitroingData.signals.reduce(
                                            (acc, cur) => acc + cur.time.length,
                                            0,
                                          )}
                                        >
                                          <div className="sticky-cell">{trapMonitroingData.used_poison || '---'}</div>
                                        </td>
                                      </>
                                    )}
                                    {timeIndex === 0 && (
                                      <td
                                        className="text-center align-top sbs-td signal-date"
                                        rowSpan={signal.time.length}
                                      >
                                        <div className="sticky-cell">
                                          {dayjs(signal.date).format('YYYY-DD-MM') || '---'}
                                        </div>
                                      </td>
                                    )}
                                    <td className="text-center align-center sbs-td signal-time">
                                      {time.inner_time || '---'}
                                    </td>
                                    <td className="text-center align-center  sbs-td">{time.bait_filling || '---'}</td>
                                    <td
                                      className={`text-center align-center  sbs-td ${+time.eaten_bait === 0 ? '' : 'text-danger'}`}
                                    >
                                      {+time.eaten_bait > 0 ? '-' : ''}
                                      {Number(time.eaten_bait).toFixed(1)}
                                    </td>
                                    <td
                                      className={`text-center align-center  sbs-td ${+time.added_bait === 0 ? '' : 'text-success'}`}
                                    >
                                      <div className="d-flex justify-content-center align-items-center">
                                        {+time.added_bait > 0 ? '+' : ''}
                                        {Number(time.added_bait).toFixed(1)}
                                        {time.bait_replacement === true && (
                                          <div className="mx-2 d-felx">
                                            <span title="Bait replacement" className="badge bg-warning">
                                              R
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td
                                      className={`text-center align-center sbs-td text-${getStatusColor(time.activity_type)}`}
                                    >
                                      {getStatus(time.activity_type)}
                                    </td>
                                  </tr>
                                )),
                            )
                        ) : (
                          <tr>
                            <td className="text-center align-top sbs-td">
                              {trapMonitroingData.sequence_number || 'N/A'}
                            </td>
                            <td className="text-center align-center sbs-td">
                              {trapMonitroingData.used_poison || 'N/A'}
                            </td>
                            <td className="text-center align-center sbs-td">---</td>
                            <td className="text-center align-center sbs-td">---</td>
                            <td className="text-center align-center  sbs-td">---</td>
                            <td className="text-center align-center  sbs-td">---</td>
                            <td className="text-center align-center  sbs-td">---</td>
                            <td className="text-center align-center  sbs-td">---</td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center align-center  sbs-td">
                          No data
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SBSMonitoring;
